// @flow
/* eslint-disable max-len */

import { del, get, patch } from '../utils/api';

// Precedence, when more than one applies, is the order listed here -- the same
// order the delete itself rejects in.
type HouseholdMemberDeleteBlockedReason =
  | 'HEAD_OF_HOUSEHOLD'
  | 'OPEN_CERTIFICATION'
  | 'COMPLETED_CERTIFICATION';

type HouseholdMemberDeleteEligibilityResponse = {
  canDelete: boolean,
  blockedReason: HouseholdMemberDeleteBlockedReason | null,
};

type HouseholdNotificationResponse = {
  type: string, // the type of notice, e.g. "NEEDS_REVIEW"
  severity: string, // the severity of the notice, e.g. "warning", "error", "info"
  message: string, // the message to display
  meta: Object, // additional information to enable smart linking (if ever implemented)
};

// Both fields are absent — the whole object is empty — when there is nothing to
// show: the ada-info flag is off, the household has no unit, or it is a prior
// resident. Read `isADA` for presence, never for truthiness.
type HouseholdUnitAccessibilityResponse = {
  isADA?: boolean,
  accessibilityTypes?: Array<string>, // already alphabetical
};

export default class HouseholdService {
  getHousehold(
    organizationId: string,
    propertyId: string,
    householdId: string,
    options?: Object,
  ): any {
    return get(
      `/${organizationId}/${propertyId}/households/${householdId}`,
      options,
    );
  }

  getPrimaryApplicant(
    organizationId: string,
    propertyId: string,
    householdId: string,
    options?: Object,
  ): any {
    return get(
      `/${organizationId}/${propertyId}/households/${householdId}/applicants/primary`,
      options,
    );
  }

  checkIfApplicantIsPrimary(
    organizationId: string,
    propertyId: string,
    householdId: string,
    applicantId: string,
    options?: Object,
  ): any {
    return get(
      `/${organizationId}/${propertyId}/households/${householdId}/applicants/${applicantId}/is-primary`,
      options,
    );
  }

  getAllHouseholds(
    organizationId: string,
    propertyId: string,
    options?: Object,
  ): any {
    return get(`/${organizationId}/${propertyId}/households`, options);
  }

  getHouseholdUserFriendlyId(householdId) {
    return get(`/households/${householdId}/user-friendly-id`);
  }

  getGrossLeaseHouseholds(
    organizationId: string,
    propertyId: string,
    filterType: string,
    limit: number = 50,
    offset: number = 0,
    options?: Object,
  ): any {
    return get(
      `/${organizationId}/${propertyId}/households/gross-leases?filterType=${filterType}&limit=${limit}&offset=${offset}`,
      options,
    );
  }

  getHouseholdNotifications(
    organizationId: string,
    propertyId: string,
    householdId: string,
    options?: Object,
  ): Promise<Array<HouseholdNotificationResponse>> {
    return get(
      `/${organizationId}/${propertyId}/households/${householdId}/notifications`,
      options,
    );
  }

  // Household-scoped twin of ResidentService.getHouseholdHasOpenCerts, for a
  // Pending Applicant household with no Resident row to key the resident-
  // scoped route off of (same Gross-Rent-excluded check, resolved by
  // householdId instead).
  getHouseholdHasOpenCerts(
    organizationId: string,
    propertyId: string,
    householdId: string,
    options?: Object,
  ): Promise<boolean> {
    return get(
      `/${organizationId}/${propertyId}/households/${householdId}/has-open-certs`,
      options,
    );
  }

  changeHeadOfHousehold(
    organizationId: string,
    propertyId: string,
    householdId: string,
    payload: {
      newPrimaryResidentId: string,
      formerPrimaryRelationshipId: string,
      formerPrimaryAffordableRelationshipId?: string,
    },
    options?: Object,
  ): Promise<mixed> {
    return patch(
      `/${organizationId}/${propertyId}/households/${householdId}/head-of-household`,
      JSON.stringify(payload),
      options,
    );
  }

  deleteHouseholdMember(
    organizationId: string,
    propertyId: string,
    householdId: string,
    residentId: string,
    options?: Object,
  ): Promise<mixed> {
    return del(
      `/${organizationId}/${propertyId}/households/${householdId}/members/${residentId}`,
      undefined,
      options,
    );
  }

  // The same three blocks deleteHouseholdMember above rejects on, read before
  // the attempt so the button can carry the reason instead of the toast.
  getHouseholdMemberDeleteEligibility(
    organizationId: string,
    propertyId: string,
    householdId: string,
    residentId: string,
    options?: Object,
  ): Promise<HouseholdMemberDeleteEligibilityResponse> {
    return get(
      `/${organizationId}/${propertyId}/households/${householdId}/members/${residentId}/delete-eligibility`,
      options,
    );
  }

  // Applicant-scoped sibling of deleteHouseholdMember above, for a Pending
  // member: its Resident/ResidentCustomer rows are only created at move-in, so
  // the resident-scoped route has nothing to address and can only 404.
  deleteHouseholdApplicant(
    organizationId: string,
    propertyId: string,
    householdId: string,
    applicantId: string,
    options?: Object,
  ): Promise<mixed> {
    return del(
      `/${organizationId}/${propertyId}/households/${householdId}/applicants/${applicantId}`,
      undefined,
      options,
    );
  }

  // Same three blocks, same response shape, evaluated against the Applicant
  // instead of the Resident.
  getHouseholdApplicantDeleteEligibility(
    organizationId: string,
    propertyId: string,
    householdId: string,
    applicantId: string,
    options?: Object,
  ): Promise<HouseholdMemberDeleteEligibilityResponse> {
    return get(
      `/${organizationId}/${propertyId}/households/${householdId}/applicants/${applicantId}/delete-eligibility`,
      options,
    );
  }

  getHouseholdUnitAccessibility(
    organizationId: string,
    propertyId: string,
    householdId: string,
    options?: Object,
  ): Promise<HouseholdUnitAccessibilityResponse> {
    return get(
      `/${organizationId}/${propertyId}/households/${householdId}/unit-accessibility`,
      options,
    );
  }
}
